import { useProductCatalogRepo, type ProductInformation } from '@hotjar/product-catalog';
import type { ProductAllowance, ExternalProductAllowances, ProductCatalogOptions } from '../types';
import { extractSiteIdFromProducts } from '../utils/extractSiteIdFromProducts';
import { useDailySessions } from './useDailySessions';

type ProductInformationWithSite = ProductInformation & { siteId: string };

function filterUndefinedProducts(products: ExternalProductAllowances) {
  if (products == null) {
    return [];
  }

  return products.filter((product) => product != null) as ProductAllowance[];
}

function isDowngradingMoreThanOnePaidSite(products: ProductInformationWithSite[]): boolean {
  const sitesToDowngrade = products.reduce((acc, product) => {
    const { siteId, plan } = product;

    if (!acc.includes(siteId) && plan.name !== 'basic') {
      acc.push(siteId);
    }

    return acc;
  }, [] as string[]);

  return sitesToDowngrade.length > 1;
}

function isDowngradingAnyObservePaidPlan(products: ProductInformationWithSite[]): boolean {
  return products.some((item) => {
    const isBusinessOrPlus = item.plan.name !== 'business' || item.plan.name !== 'plus';

    return item.product.name === 'observe' && isBusinessOrPlus;
  });
}

/**
 * Hook that extracts product's information and its associated siteId
 * from the products to downgrade SKUs.
 */
function useProductsInformation(
  productsToDowngrade: ProductAllowance[],
  productCatalogOptions: ProductCatalogOptions,
): ProductInformationWithSite[] {
  const { getProductBySku } = useProductCatalogRepo(productCatalogOptions);

  return productsToDowngrade.reduce(
    (
      acc: ProductInformationWithSite[],
      productInformationWithSite,
    ): ProductInformationWithSite[] => {
      const {
        sku,
        entity: { entityIdentifier },
      } = productInformationWithSite;
      const productInformation = getProductBySku(sku);

      if (!productInformation) return acc;

      acc.push({ ...productInformation, siteId: entityIdentifier });
      return acc;
    },
    [],
  );
}

/**
 * Hook that decides if the customer can is within the cohort to be able
 * to see the Churn Deflection Modal V1 expeiment
 * Only applies for customers that try to downgrade:
 *  - Only one paid site that has at least an observe (plus or business) plan.
 *    This excludes only Ask downgrades for now.
 *  - If the current observe daily sessions are more than 100.
 */
export function useCanSeeExperimentChurnDeflectionModalV1(
  productsToDowngrade: ExternalProductAllowances,
  productCatalogOptions: ProductCatalogOptions,
): boolean {
  const filteredProducts = filterUndefinedProducts(productsToDowngrade);
  const siteId = extractSiteIdFromProducts(filteredProducts);

  const { data: dailySessions = 0 } = useDailySessions(siteId);
  const products = useProductsInformation(filteredProducts, productCatalogOptions);

  if (isDowngradingMoreThanOnePaidSite(products)) {
    return false;
  }

  if (!isDowngradingAnyObservePaidPlan(products)) {
    return false;
  }

  if (dailySessions <= 100) {
    return false;
  }

  return true;
}
